import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getDocsFromServer,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { useEffect, useState, useRef } from "react";
import { setCallsData } from "../Redux/calls/action";
import { setLocationsData } from "../Redux/locations/action";
import { setAccountProfiles } from "../Redux/profile/action";
import { setErrorStatus } from "../Redux/status/action";
import { auth, database } from "./firebase";
import { uploadToBucket } from "./storage";
import { isValidObject } from "./validators";
import { logout } from "../Redux/authentication/actions";

const PROFILE_DELETED_MESSAGE = "Your account has been deleted";
const PROFILE_NOT_FOUND_MESSAGE = "Account not found. Please contact support";
// Bounded wait for a first cache/server confirmation of the profile before
// falling back to a retry affordance instead of spinning indefinitely.
export const PROFILE_SYNC_TIMEOUT_MS = 15000;

/**
 * Soft-delete marker, always written by the backend as `+new Date()`
 * (functions/libs/repository.js). Tolerant of Timestamp/Date in case that
 * shape ever changes - a missed deletion is worse than an overly strict
 * type check.
 */
function isDeletedProfile(data) {
  const deletedAt = data?.actions?.deletedAt;
  if (typeof deletedAt === "number") {
    return deletedAt > 0;
  }
  if (deletedAt instanceof Date) {
    return true;
  }
  if (deletedAt && typeof deletedAt.toMillis === "function") {
    return true;
  }
  return false;
}

/**
 * Pure snapshot reducer - exported so it can be unit tested with fake
 * snapshots. A doc without `actions.deletedAt` always wins over one that
 * has it (matches the backend filter semantics in
 * functions/libs/feature/profile.js) for the rare case of duplicate docs
 * sharing a phone number.
 */
export function resolveProfileFromSnapshot(dataSnapshot) {
  let activeProfile = null;
  let deletedProfile = null;

  dataSnapshot.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    if (isDeletedProfile(data)) {
      if (deletedProfile === null) {
        deletedProfile = { ...data };
      }
    } else if (activeProfile === null) {
      activeProfile = { ...data };
    }
  });

  return {
    matchedCount: dataSnapshot.size,
    activeProfile: activeProfile,
    deletedProfile: deletedProfile
  };
}

/**
 * Keeps the employee's `employees` doc in sync with Redux and forces a
 * logout ONLY on a positive, server-confirmed signal of deletion - never on
 * an empty result, which a warm-but-unsynced local cache produces routinely
 * (see PR notes). Employees are only ever soft-deleted (firestore.rules
 * disallows hard delete), so a doc that matches the phone number always
 * still exists; the only way this hook decides "deleted" is a doc that
 * actually carries `actions.deletedAt`.
 *
 * KEEP IN SYNC with app/employee/src/Services/database.js useProfileListener
 * (and vice-versa) - the two copies differ only in createLogout's `app` tag.
 *
 * @returns {"pending"|"ready"|"unreachable"} sync state for the caller to
 * gate a "Connecting..." screen on when there is no profile data yet.
 */
export function useProfileListener(props) {
  const listenerRef = useRef(null);
  const forcedLogoutRef = useRef(false);
  const serverCheckInFlightRef = useRef(false);
  const cacheMissReportedRef = useRef(false);
  const lastProfileRef = useRef(null);
  const [syncState, setSyncState] = useState("pending");

  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;

    const publishProfile = (profile) => {
      // includeMetadataChanges raises events for metadata-only changes;
      // dedupe so an unchanged doc doesn't re-fire downstream effects on
      // every flap.
      const serialized = JSON.stringify(profile);
      if (serialized !== lastProfileRef.current) {
        lastProfileRef.current = serialized;
        setAccountProfiles(profile);
      }
    };

    const resolveSync = () => {
      if (cancelled) {
        return;
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      setSyncState("ready");
    };

    const teardown = () => {
      if (typeof listenerRef.current === "function") {
        listenerRef.current();
      }
      listenerRef.current = null;
    };

    const forceLogout = async (status, from, context) => {
      if (forcedLogoutRef.current === true) {
        return;
      }
      forcedLogoutRef.current = true;
      resolveSync();

      // Audit BEFORE signOut so the write can't be cancelled by the token drop.
      await createLogout(status, from, props.accessToken, context);

      // Stop listening first so a stray snapshot can't race the RESET.
      teardown();
      publishProfile(null);

      logout(status);
    };

    /**
     * Cache-bypassing second opinion for an empty server-sourced result. A
     * network failure must never be treated as proof of deletion.
     * @returns {Promise<{state: "active"|"deleted"|"missing"|"inconclusive", profile?: object}>}
     */
    const confirmWithServer = async (phoneNumber) => {
      try {
        const serverSnapshot = await getDocsFromServer(
          query(
            collection(database, "employees"),
            where("phoneNumber", "==", phoneNumber)
          )
        );
        const resolved = resolveProfileFromSnapshot(serverSnapshot);
        if (resolved.activeProfile !== null) {
          return { state: "active", profile: resolved.activeProfile };
        }
        if (resolved.deletedProfile !== null) {
          return { state: "deleted" };
        }
        return { state: "missing" };
      } catch (error) {
        console.error("[Profile Listener] server confirmation failed:", error);
        return { state: "inconclusive" };
      }
    };

    const handleEmptyServerResult = async (phoneNumber) => {
      if (serverCheckInFlightRef.current === true) {
        return;
      }
      serverCheckInFlightRef.current = true;
      try {
        // Stale redux-persist phone number. Self-heals: useAuthSubscriber
        // re-pushes the live phoneNumber on every onIdTokenChanged, which
        // re-runs this effect with the correct value.
        const signedInPhoneNumber = auth.currentUser?.phoneNumber || null;
        if (
          signedInPhoneNumber !== null &&
          signedInPhoneNumber !== phoneNumber
        ) {
          console.warn(
            "[Profile Listener] phoneNumber mismatch, ignoring empty result"
          );
          resolveSync();
          await createLogout(
            { code: "custom", message: "phoneNumber mismatch" },
            "useProfileListener/phoneNumberMismatch",
            props.accessToken,
            { queriedPhoneNumber: phoneNumber, type: "diagnostic" }
          );
          return;
        }

        const confirmation = await confirmWithServer(phoneNumber);

        if (confirmation.state === "active") {
          resolveSync();
          publishProfile(confirmation.profile);
          return;
        }
        if (confirmation.state === "inconclusive") {
          // Proves nothing about deletion - never log out.
          return;
        }
        if (confirmation.state === "deleted") {
          await forceLogout(
            { code: "custom", message: PROFILE_DELETED_MESSAGE },
            "useProfileListener/accountDeleted",
            { source: "serverConfirmation" }
          );
          return;
        }
        await forceLogout(
          { code: "custom", message: PROFILE_NOT_FOUND_MESSAGE },
          "useProfileListener/profileNotFound",
          { source: "serverConfirmation", queriedPhoneNumber: phoneNumber }
        );
      } finally {
        serverCheckInFlightRef.current = false;
      }
    };

    const subscribeToProfile = (phoneNumber) => {
      const profileQuery = query(
        collection(database, "employees"),
        where("phoneNumber", "==", phoneNumber)
      );

      return onSnapshot(
        profileQuery,
        // Required: without this, an empty-cache -> empty-server transition
        // is a metadata-only change and raises no second event, so a
        // genuinely missing profile would never be detected (row 4 below).
        { includeMetadataChanges: true },
        async (dataSnapshot) => {
          const fromCache = dataSnapshot.metadata.fromCache;
          const resolved = resolveProfileFromSnapshot(dataSnapshot);

          // 1. Active profile - works identically online and offline.
          if (resolved.activeProfile !== null) {
            cacheMissReportedRef.current = false;
            resolveSync();
            publishProfile(resolved.activeProfile);
            return;
          }

          // 2. Positive deletion signal. A local cache can drop documents
          //    but cannot fabricate a numeric actions.deletedAt, so this is
          //    trusted even when fromCache is true.
          if (resolved.deletedProfile !== null) {
            await forceLogout(
              { code: "custom", message: PROFILE_DELETED_MESSAGE },
              "useProfileListener/accountDeleted",
              { source: fromCache ? "cacheSnapshot" : "serverSnapshot" }
            );
            return;
          }

          // 3. Empty AND unsynced. THIS WAS THE BUG: an empty cache result
          //    is never a deletion signal - keep whatever profile is
          //    already loaded and wait for the server to weigh in.
          if (fromCache === true) {
            console.warn(
              "[Profile Listener] empty snapshot from cache - awaiting server"
            );
            if (cacheMissReportedRef.current === false) {
              cacheMissReportedRef.current = true;
              await createLogout(
                { code: "custom", message: "empty snapshot from cache" },
                "useProfileListener/emptyCacheSnapshot",
                props.accessToken,
                { queriedPhoneNumber: phoneNumber, type: "diagnostic" }
              );
            }
            return;
          }

          // 4. Server-confirmed empty. Disambiguate before any logout.
          await handleEmptyServerResult(phoneNumber);
        },
        async (error) => {
          console.error("[Profile Listener Error]:", error);

          // permission-denied here is almost always a stale phone number or
          // claim gap, not a deletion. Routing it to setErrorStatus would
          // self-logout via the status saga's permission-denied branch.
          if (error?.code === "permission-denied") {
            await createLogout(
              { code: error.code, message: error.message },
              "useProfileListener/permissionDenied",
              props.accessToken,
              { queriedPhoneNumber: phoneNumber, type: "diagnostic" }
            );
            return;
          }

          setErrorStatus(error, "useProfileListener");
        }
      );
    };

    // Attach guard: truthy check (not `=== null`) so an `undefined` return
    // from a falsy phoneNumber can never latch the listener off permanently.
    if (
      props.isAuth === true &&
      typeof props.phoneNumber === "string" &&
      props.phoneNumber.length > 0 &&
      !listenerRef.current
    ) {
      forcedLogoutRef.current = false;
      cacheMissReportedRef.current = false;
      lastProfileRef.current = null;
      setSyncState("pending");
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          setSyncState((current) =>
            current === "pending" ? "unreachable" : current
          );
        }
      }, PROFILE_SYNC_TIMEOUT_MS);
      listenerRef.current = subscribeToProfile(props.phoneNumber) || null;
    } else if (!props.isAuth && listenerRef.current) {
      teardown();
    }

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      teardown();
    };
    // eslint-disable-next-line
  }, [props.isAuth, props.phoneNumber, props.accessToken]);

  return syncState;
}

/**
 * Audit trail for forced logouts and near-miss diagnostics, mirroring
 * app/employee/src/Services/database.js. `context.type === "diagnostic"`
 * rows are one-shot per listener attach and are NOT forced logouts - filter
 * them out of logout-frequency dashboards.
 */
export async function createLogout(error, from, accessToken, context) {
  try {
    const logoutRef = collection(database, "logout");

    await addDoc(logoutRef, {
      createdAt: +new Date(),
      serverTime: serverTimestamp(),
      app: "support",
      error: {
        from: from || null,
        code: error?.code || null,
        message: error?.message || null
      },
      accessToken: accessToken || null,
      ...(context ? { context: context } : {})
    });
  } catch (error) {
    console.error("error from createLogout:", error);
  }
}

export function useLocationListener(props) {
  const [locationListener, setLocationListener] = useState({
    listener: null
  });

  const subscribeToLocations = () => {
    const profileQuery = query(
      collection(database, "locations"),
      where("access", "==", "public")
    );

    return onSnapshot(
      profileQuery,
      (dataSnapshot) => {
        let locations = {};
        dataSnapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.actions?.deletedAt) {
            locations[doc.id] = {
              ...data,
              documentId: doc.id
            };
          }
        });
        setLocationsData(locations);
      },
      (error) => {
        console.error(error, "from locations");
        setErrorStatus(error);
      }
    );
  };
  useEffect(() => {
    if (
      props.isAuth === true &&
      typeof props.uid === "string" &&
      locationListener.listener === null
    ) {
      setLocationListener({
        listener: subscribeToLocations()
      });
    } else if (
      props.isAuth === false &&
      typeof locationListener.listener === "function"
    ) {
      locationListener.listener();
      setLocationListener({
        listener: null
      });
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.uid]);
}

export function useCallsListener(props) {
  const [callsListener, setCallsListener] = useState({
    listener: null
  });

  const subscribeToComplaints = (phoneNumber) => {
    const callsQuery = query(
      collection(database, "calls"),
      where("employee.phoneNumber", "==", phoneNumber),
      where("status", "in", ["Answered", "completed"]),
      where("createdAt", ">", +new Date() - 86400000),
      orderBy("createdAt", "desc")
    );

    return onSnapshot(
      callsQuery,
      (dataSnapshot) => {
        let calls = {};
        dataSnapshot.forEach((doc) => {
          calls[doc.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });
        setCallsData(calls);
      },
      (error) => {
        console.error(error, "from calls");
        setErrorStatus(error);
      }
    );
  };
  useEffect(() => {
    if (
      props.isAuth === true &&
      typeof props.phoneNumber === "string" &&
      typeof props.uid === "string" &&
      callsListener.listener === null
    ) {
      setCallsListener({
        listener: subscribeToComplaints(props.phoneNumber)
      });
    } else if (
      props.isAuth === false &&
      typeof callsListener.listener === "function"
    ) {
      callsListener.listener();
      setCallsListener({
        listener: null
      });
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.phoneNumber, props.uid]);
}

export async function updateLinkCompliant(compliantId, data) {
  const rootRef = doc(database, "complaints", compliantId);
  await updateDoc(rootRef, {
    ...data,
    "issuedBy.linkedWith": arrayUnion(data["issuedBy.linkedWith"]),
    latestUpdate: arrayUnion(data.latestUpdate)
  });
}

export async function updateAccessSyncStatus(employeeId, status) {
  const rootRef = doc(database, "employees", employeeId);

  await updateDoc(rootRef, {
    accessSyncStatus: status
  });
}

export async function getComplaint(complaintId, isIndependentEngineer) {
  const docRef = doc(database, "complaints", complaintId);
  const docSnapshot = await getDoc(docRef);
  const complaintData = docSnapshot.data();
  if (
    isValidObject(complaintData) &&
    (complaintData?.status?.currentStatus === "OPEN" ||
      (isIndependentEngineer &&
        complaintData?.status?.currentStatus === "PENDING CLOSURE"))
  ) {
    return { ...complaintData, documentId: docSnapshot.id };
  } else {
    return null;
  }
}

export async function getComplaintsTotalCount(props) {
  let complaintsRef = query(
    collection(database, "complaints"),
    where("status.currentStatus", "==", "OPEN")
  );

  if (props.filters?.length > 0) {
    props.filters?.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        complaintsRef = query(
          complaintsRef,
          where(props.filters[index - 2], props.filters[index - 1], value)
        );
      }
    });
  }

  const count = await getCountFromServer(complaintsRef);
  return count.data().count;
}

export async function getExistingComplaint(locationId, category) {
  const complaintQuery = query(
    collection(database, "complaints"),
    where("location.id", "==", locationId),
    where("category", "==", category),
    where("status.currentStatus", "!=", "CLOSED")
  );
  const querySnapshot = await getDocs(complaintQuery);
  const complaints = [];
  querySnapshot.forEach((doc) => {
    complaints.push({ documentId: doc.id, ...doc.data() });
  });
  return complaints;
}

export async function setComplaintStatusToOpen(data, files) {
  const { complaintId, description, employee, locationId } = data;

  const rootRef = doc(database, "complaints", complaintId);

  const proof = [];

  for (const file of files) {
    const filePath = await uploadToBucket(
      `/${locationId}/complaints/${complaintId}/closure/${file?.name}`,
      file
    );
    proof.push(filePath);
  }

  const updatedAt = +new Date();
  await updateDoc(rootRef, {
    status: {
      currentStatus: "OPEN",
      updatedAt
    },
    latestUpdate: arrayUnion({
      proof,
      description,
      updatedBy: employee,
      updatedAt,
      currentStatus: "OPEN",
      previousStatus: "PENDING CLOSURE"
    })
  });
}
